import { useContext } from 'react';

import { OrganizationContext } from '@/contexts/organization-context-object';
import { parseClientOrgProfile } from '@/hooks/use-org-profile';

import type { OfficeType } from '@/hooks/use-org-profile';

/**
 * The office's type, read from the membership row the organisation provider already holds
 * (`organizations.settings.org_profile`, selected by the `user-organizations` query). That query
 * is not persisted, so a reload reads the row afresh. In a session that did not make the change it
 * has a five-minute stale time and refetches on window focus: the flip shows at the first
 * tab-visible event after the row is five minutes old, or on reload. The persisted `org-profile`
 * query has no such refetch, so it is not used. Government unless the row says `corporate`: an
 * unknown office, an unknown value, or no provider all read as government.
 */
export function useOfficeType(organizationId: string | null | undefined): OfficeType {
  const context = useContext(OrganizationContext);
  const current = context?.currentOrganization;
  const organization =
    current?.id === organizationId
      ? current
      : context?.userOrganizations?.find((member) => member.organization_id === organizationId)
          ?.organization;
  return parseClientOrgProfile(organization?.settings?.org_profile)?.officeType ?? 'government';
}

export function useIsCorporateOffice(organizationId: string | null | undefined): boolean {
  return useOfficeType(organizationId) === 'corporate';
}
